import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { clamp } from 'lodash'
import Image from 'next/image'
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'

import Button from '@/components/base/Button'
// import Button from '@/components/base/Button'
import Grid, { GridProps } from '@/components/base/Grid'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { SectionHandler } from '@/components/sections/HeroSection'
// import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type Slide = {
  title?: string
  paragraph?: string
  icon?: WPImage
}

export type CarouselProps = GridProps & {
  slides: Slide[]
  button?: WPLink
  variant?: 'vertical' | 'horizontal'
}

const Carousel = forwardRef<SectionHandler, CarouselProps>(
  ({ variant = 'horizontal', slides, button, ...props }, ref) => {
    // const { strings } = useStore()

    const root = useRef<HTMLDivElement>(null)
    const target = useRef<HTMLDivElement>(null!)
    const icons = useRef<HTMLDivElement>(null!)
    const contents = useRef<HTMLDivElement>(null!)
    const swiper = useRef<Swiper>(null!)

    const swiperButtonPrev = useRef<HTMLButtonElement>(null!)
    const swiperButtonNext = useRef<HTMLButtonElement>(null!)
    const swiperPagination = useRef<HTMLDivElement>(null!)

    const contentsSetter = useRef<Function>(null!)
    const iconsSetter = useRef<Function>(null!)

    const { context, contextSafe } = useGSAP(() => {}, { scope: root })

    useIsomorphicLayoutEffect(() => {
      contentsSetter.current = gsap.quickSetter(contents.current, 'x', 'px')
      iconsSetter.current = gsap.quickSetter(icons.current, 'x', 'px')
    }, [])

    useIsomorphicLayoutEffect(() => {
      swiper.current = new Swiper(target.current, {
        modules: [Navigation, Pagination],
        navigation: {
          nextEl: swiperButtonNext.current,
          prevEl: swiperButtonPrev.current
        },
        pagination: {
          el: swiperPagination.current,
          clickable: true
        }
      })

      const handleSliderMove = () => {
        useLayoutStore.getState().setState({ swiperActive: true })
        gsap.set([icons.current, contents.current], { transitionDuration: `0ms` })
      }

      const handleSlideChangeTransition = () => {
        gsap.set([icons.current, contents.current], { transitionDuration: `500ms` })
      }

      const handleSlideChangeTransitionEnd = () => {
        useLayoutStore.getState().setState({ swiperActive: false })
      }

      const handleSlideChangeProgress = () => {
        const length = slides.length - 1
        const progress = swiper.current.progress
        const ratio = clamp(window.innerWidth / 1440, 1, 1.15)
        const gutter = Math.max(24, 24 * ratio)

        const contentsWidth = contents.current.clientWidth
        const iconsWidth = icons.current.clientWidth

        const contentPosition = (contentsWidth + gutter) * length * progress
        const iconsPosition = iconsWidth * length * progress

        contentsSetter.current(-contentPosition)
        iconsSetter.current(-iconsPosition)
      }

      swiper.current.on('sliderMove', handleSliderMove)
      swiper.current.on('progress', handleSlideChangeProgress)
      swiper.current.on('slideChangeTransitionStart', handleSlideChangeTransition)
      swiper.current.on('slideChangeTransitionEnd', handleSlideChangeTransitionEnd)
      swiper.current.on('slideResetTransitionStart', handleSlideChangeTransition)
      swiper.current.on('slideResetTransitionEnd', handleSlideChangeTransitionEnd)

      return () => {
        swiper.current.off('sliderMove', handleSliderMove)
        swiper.current.off('progress', handleSlideChangeProgress)
        swiper.current.off('slideChangeTransitionStart', handleSlideChangeTransition)
        swiper.current.off('slideChangeTransitionEnd', handleSlideChangeTransitionEnd)
        swiper.current.off('slideResetTransitionStart', handleSlideChangeTransition)
        swiper.current.off('slideResetTransitionEnd', handleSlideChangeTransitionEnd)
        swiper.current.destroy()
      }
    }, [])

    useIsomorphicLayoutEffect(() => {
      const unsubscribe = useLayoutStore.subscribe(
        (state) => state.scrollDirLocked,
        (scrollDirLocked) =>
          scrollDirLocked === 'y' ? swiper.current.disable() : swiper.current.enable()
      )

      return () => {
        unsubscribe()
      }
    }, [])

    /**
     * Timelines
     */
    const createTimeline = contextSafe(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: { ease: 'none' }
      })

      return timeline
    })

    const createEnterTimeline = contextSafe(() => {
      const icons = context.selector?.('[data-motion-icons]')[0]
      const nav = context.selector?.('[data-motion-nav]')[0]
      const body = context.selector?.('[data-motion-body]')[0]
      const dots = context.selector?.('[data-motion-dots]')[0]
      const button = context.selector?.('[data-motion-button]')[0]

      const timeline = gsap.timeline({
        paused: true,
        defaults: { duration: 1, ease: 'power3.out', stagger: { amount: 0.5 } }
      })

      timeline.from([icons, nav, body, dots, button], { opacity: 0, y: 50 })

      return timeline
    })

    const createLeaveTimeline = contextSafe(() => {
      const icons = context.selector?.('[data-motion-icons]')[0]
      const nav = context.selector?.('[data-motion-nav]')[0]
      const body = context.selector?.('[data-motion-body]')[0]
      const dots = context.selector?.('[data-motion-dots]')[0]
      const button = context.selector?.('[data-motion-button]')[0]

      const timeline = gsap.timeline({
        paused: true,
        defaults: {
          duration: 1,
          ease: 'power3.in',
          immediateRender: false,
          stagger: { amount: 0.5 }
        }
      })

      timeline.to([icons, nav, body, dots, button], { opacity: 0, y: -50 })

      return timeline
    })

    const resetTimeline = contextSafe(() => {
      const icons = context.selector?.('[data-motion-icons]')[0]
      const nav = context.selector?.('[data-motion-nav]')[0]
      const body = context.selector?.('[data-motion-body]')[0]
      const dots = context.selector?.('[data-motion-dots]')[0]
      const button = context.selector?.('[data-motion-button]')[0]

      gsap.set([icons, nav, body, dots, button], { clearProps: 'opacity,y' })
    })

    useImperativeHandle(
      ref,
      () => ({
        createTimeline,
        createEnterTimeline,
        createLeaveTimeline,
        resetTimeline
      }),
      [createTimeline, createEnterTimeline, createLeaveTimeline, resetTimeline]
    )

    return (
      <Grid ref={root} className="px-[2rem] md:px-0" {...props}>
        <div
          className={cn(
            'relative col-span-4 grid grid-cols-4 items-center gap-x-gutter gap-y-spacing-lg md:col-start-2',
            [
              {
                'lg:col-span-4 lg:col-start-8 lg:grid-cols-4 xl:col-span-3 xl:col-start-9 xl:grid-cols-3':
                  variant === 'vertical',
                'lg:col-span-6 lg:col-start-5 lg:grid-cols-6 xl:col-span-5 xl:col-start-6 xl:grid-cols-5':
                  variant === 'horizontal'
              }
            ]
          )}
        >
          <div className="col-span-2">
            <div className="relative max-w-[208px] pb-[110%]" data-motion-icons>
              <SVGIcon
                symbol="shape-outline"
                className="absolute left-0 top-0 h-full w-full fill-none stroke-white-dark"
              />
              <div className="absolute bottom-0 left-[0.2rem] right-[0.2rem] top-0 flex overflow-hidden">
                <div
                  ref={icons}
                  className="flex w-full transition-transform duration-500 ease-out-cubic"
                >
                  {slides.map((slide, index) => (
                    <div
                      key={`slide-content-${index}`}
                      className="relative flex w-full shrink-0 basis-full flex-col gap-y-spacing-sm overflow-hidden"
                    >
                      {slide.icon?.url ? (
                        <Image
                          src={slide.icon?.url || ''}
                          alt={slide.icon?.alt || ''}
                          fill
                          className="object-contain"
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="static col-span-4 grid gap-y-spacing-sm md:gap-y-spacing-lg lg:col-span-4 xl:col-span-3">
            <div
              className="relative z-10 hidden items-center space-x-spacing-xxs lg:flex"
              data-motion-nav
            >
              <button
                ref={swiperButtonPrev}
                className="-m-[1.2rem] flex p-[1.2rem] transition-opacity duration-300 ease-in-out-cubic disabled:opacity-20"
                tabIndex={-1}
              >
                <span className="sr-only">prev</span>
                <SVGIcon
                  symbol="arrow-prev"
                  className="h-[1.8rem] w-[1.9rem] fill-surface-primary-dark"
                />
              </button>

              <button
                ref={swiperButtonNext}
                className="-m-[1.2rem] flex p-[1.2rem] transition-opacity duration-300 ease-out-cubic disabled:opacity-20"
                tabIndex={-1}
              >
                <span className="sr-only">next</span>
                <SVGIcon
                  symbol="arrow-next"
                  className="h-[1.7rem] w-[1.6rem] fill-surface-primary-dark"
                />
              </button>
            </div>

            <div className="flex overflow-hidden py-[0.1em]" data-motion-body>
              <div
                ref={contents}
                className="flex h-full w-full transition-transform duration-500 ease-out-cubic"
              >
                {slides.map((slide, index) => (
                  <div
                    key={`slide-content-${index}`}
                    className="mr-spacing-sm flex min-h-0 w-auto shrink-0 grow-0 basis-full flex-col gap-y-spacing-sm"
                  >
                    <div className="flex">
                      <Typography variant="large" component="h6" className="font-medium">
                        {slide?.title}
                      </Typography>
                    </div>
                    <div className="flex">
                      <Typography variant="regular" paragraph className="">
                        {slide?.paragraph}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              ref={swiperPagination}
              className={cn([
                'relative z-10 -ml-[0.4rem] flex',
                '[&_.swiper-pagination-bullet]:-my-[0.8rem] [&_.swiper-pagination-bullet]:flex [&_.swiper-pagination-bullet]:cursor-pointer [&_.swiper-pagination-bullet]:p-[0.4rem]',
                '[&_.swiper-pagination-bullet]:after:h-[.5rem] [&_.swiper-pagination-bullet]:after:w-[.5rem] [&_.swiper-pagination-bullet]:after:rounded-full [&_.swiper-pagination-bullet]:after:bg-surface-primay-invert-light [&_.swiper-pagination-bullet]:after:opacity-20',
                '[&_.swiper-pagination-bullet]:after:transition-opacity [&_.swiper-pagination-bullet]:after:duration-500 [&_.swiper-pagination-bullet]:after:ease-out-cubic',
                '[&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:after:opacity-100'
              ])}
              data-motion-dots
            />

            {button?.url ? (
              <div className="relative z-10 flex" data-motion-button>
                <Button
                  endIcon={
                    <SVGIcon
                      symbol="arrow-right"
                      className="h-[0.8rem] w-[4rem] fill-surface-primay-invert-dark"
                    />
                  }
                  tabIndex={-1}
                >
                  <span className="label" data-label={button.title}>
                    <Typography variant="regular">{button.title}</Typography>
                  </span>
                </Button>
              </div>
            ) : null}
          </div>

          <div
            ref={target}
            className="absolute left-0 top-0 h-full w-full select-none overflow-hidden"
          >
            <div className="swiper-wrapper flex h-full w-auto touch-pan-y">
              {slides.map((slide, index) => (
                <div
                  key={`slide-${index}`}
                  className="swiper-slide flex min-h-0 w-auto shrink-0 grow-0 basis-full items-center justify-center"
                />
              ))}
            </div>
          </div>
        </div>
      </Grid>
    )
  }
)

export default Carousel
